import { Capacitor } from '@capacitor/core';
import {
  CapacitorSQLite,
  SQLiteConnection,
  type SQLiteDBConnection,
} from '@capacitor-community/sqlite';
import { generateFieldId } from '@/modules/field/utils/generateId';
import {
  isOfflineEntityTable,
  migrationStatements,
  SQLITE_DB_NAME,
  SQLITE_DB_VERSION,
  type OfflineEntityTable,
} from '@/services/database/migrations';

export type SyncStatus = 'pending' | 'synced' | 'conflict' | 'error';

export type OfflineRecord = {
  id: string;
  sync_status: SyncStatus;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  data: Record<string, unknown>;
  server_id?: number | null;
  version?: number;
};

type SqlValue = string | number | null;

let connectionPromise: Promise<SQLiteDBConnection> | null = null;
let sqliteConnection: SQLiteConnection | null = null;
let webStoreReady = false;
let schemaReady: Promise<void> | null = null;

async function applyMigrations(db: SQLiteDBConnection): Promise<void> {
  if (!schemaReady) {
    schemaReady = (async () => {
      for (const statement of migrationStatements()) {
        await db.execute(statement);
      }
    })().catch((error) => {
      schemaReady = null;
      throw error;
    });
  }

  await schemaReady;
}

function nowIso(): string {
  return new Date().toISOString();
}

function parseJson<T>(raw: string | null | undefined, fallback: T): T {
  if (!raw) {
    return fallback;
  }

  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

async function ensureWebStore(sqlite: SQLiteConnection): Promise<void> {
  if (webStoreReady || Capacitor.getPlatform() !== 'web') {
    return;
  }

  if (typeof document !== 'undefined' && !document.querySelector('jeep-sqlite')) {
    const jeepEl = document.createElement('jeep-sqlite');
    document.body.appendChild(jeepEl);
    await customElements.whenDefined('jeep-sqlite');
  }

  await sqlite.initWebStore();
  webStoreReady = true;
}

function isConnectionAlreadyExistsError(error: unknown): boolean {
  const message =
    error instanceof Error ? error.message : typeof error === 'string' ? error : '';

  return message.toLowerCase().includes('already exists');
}

async function openConnection(): Promise<SQLiteDBConnection> {
  if (!sqliteConnection) {
    sqliteConnection = new SQLiteConnection(CapacitorSQLite);
  }

  await ensureWebStore(sqliteConnection);

  // Após um reload do webview (ex.: logout/relogin), o dicionário de conexões
  // do lado JS volta vazio, mas a conexão nativa pode continuar registrada.
  // Reconciliar os dois lados evita o erro "Connection ... already exists".
  try {
    await sqliteConnection.checkConnectionsConsistency();
  } catch {
    // sem conexões para reconciliar — segue normalmente.
  }

  const isConn = (await sqliteConnection.isConnection(SQLITE_DB_NAME, false)).result;

  let db: SQLiteDBConnection;

  if (isConn) {
    db = await sqliteConnection.retrieveConnection(SQLITE_DB_NAME, false);
  } else {
    try {
      db = await sqliteConnection.createConnection(
        SQLITE_DB_NAME,
        false,
        'no-encryption',
        SQLITE_DB_VERSION,
        false,
      );
    } catch (error) {
      // Conexão nativa remanescente de uma sessão anterior: reutiliza em vez
      // de quebrar o login.
      if (isConnectionAlreadyExistsError(error)) {
        db = await sqliteConnection.retrieveConnection(SQLITE_DB_NAME, false);
      } else {
        throw error;
      }
    }
  }

  await db.open();

  return db;
}

async function withDb<T>(fn: (db: SQLiteDBConnection) => Promise<T>): Promise<T> {
  if (!connectionPromise) {
    connectionPromise = openConnection();
  }

  let db: SQLiteDBConnection;

  try {
    db = await connectionPromise;
  } catch (error) {
    // Não deixa um erro de abertura ficar em cache: a próxima chamada tenta de novo.
    connectionPromise = null;
    throw error;
  }

  await applyMigrations(db);

  return fn(db);
}

export async function initializeDatabase(): Promise<void> {
  await withDb(async () => undefined);
}

function rowToRecord(table: OfflineEntityTable, row: Record<string, unknown>): OfflineRecord {
  const data = parseJson<Record<string, unknown>>(String(row.data ?? '{}'), {});

  const record: OfflineRecord = {
    id: String(row.id),
    sync_status: (row.sync_status as SyncStatus) ?? 'pending',
    created_at: String(row.created_at),
    updated_at: String(row.updated_at),
    deleted_at: row.deleted_at ? String(row.deleted_at) : null,
    data,
  };

  if (table === 'field_reports') {
    record.server_id =
      row.server_id === null || row.server_id === undefined
        ? null
        : Number(row.server_id);
    record.version = Number(row.version ?? 0);
  }

  return record;
}

export class DatabaseService {
  async init(): Promise<void> {
    await initializeDatabase();
  }

  async insert<T extends Record<string, unknown>>(
    table: OfflineEntityTable,
    data: T,
    id?: string,
  ): Promise<OfflineRecord> {
    if (!isOfflineEntityTable(table)) {
      throw new Error(`Tabela não permitida: ${table}`);
    }

    const recordId = id ?? generateFieldId();
    const timestamp = nowIso();

    await withDb(async (db) => {
      if (table === 'field_reports') {
        await db.run(
          `INSERT INTO field_reports (id, sync_status, server_id, version, data, created_at, updated_at, deleted_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, NULL)`,
          [
            recordId,
            'pending',
            null,
            0,
            JSON.stringify(data),
            timestamp,
            timestamp,
          ],
        );
      } else {
        await db.run(
          `INSERT INTO proposals (id, sync_status, data, created_at, updated_at, deleted_at)
           VALUES (?, ?, ?, ?, ?, NULL)`,
          [recordId, 'pending', JSON.stringify(data), timestamp, timestamp],
        );
      }
    });

    const created = await this.findById(table, recordId);
    if (!created) {
      throw new Error('Falha ao persistir registro local.');
    }

    return created;
  }

  async update<T extends Record<string, unknown>>(
    table: OfflineEntityTable,
    id: string,
    data: Partial<T>,
    options?: { markPending?: boolean },
  ): Promise<OfflineRecord | null> {
    const existing = await this.findById(table, id, { includeDeleted: true });
    if (!existing) {
      return null;
    }

    const merged = { ...existing.data, ...data };
    const timestamp = nowIso();
    const syncStatus: SyncStatus = options?.markPending === false ? existing.sync_status : 'pending';

    await withDb(async (db) => {
      if (table === 'field_reports') {
        await db.run(
          `UPDATE field_reports
           SET data = ?, updated_at = ?, sync_status = ?, deleted_at = NULL
           WHERE id = ?`,
          [JSON.stringify(merged), timestamp, syncStatus, id],
        );
      } else {
        await db.run(
          `UPDATE proposals
           SET data = ?, updated_at = ?, sync_status = ?, deleted_at = NULL
           WHERE id = ?`,
          [JSON.stringify(merged), timestamp, syncStatus, id],
        );
      }
    });

    return this.findById(table, id);
  }

  /** Soft delete: preenche deleted_at e marca pending para propagar ao servidor. */
  async delete(table: OfflineEntityTable, id: string): Promise<boolean> {
    const existing = await this.findById(table, id, { includeDeleted: true });
    if (!existing) {
      return false;
    }

    const timestamp = nowIso();

    await withDb(async (db) => {
      await db.run(
        `UPDATE ${table}
         SET deleted_at = ?, updated_at = ?, sync_status = 'pending'
         WHERE id = ?`,
        [timestamp, timestamp, id],
      );
    });

    return true;
  }

  async findAll(
    table: OfflineEntityTable,
    options?: { includeDeleted?: boolean },
  ): Promise<OfflineRecord[]> {
    const whereDeleted =
      options?.includeDeleted === true ? '' : ' WHERE deleted_at IS NULL';

    return withDb(async (db) => {
      const result = await db.query(`SELECT * FROM ${table}${whereDeleted} ORDER BY updated_at DESC`);
      const rows = (result.values ?? []) as Record<string, unknown>[];

      return rows.map((row) => rowToRecord(table, row));
    });
  }

  async findById(
    table: OfflineEntityTable,
    id: string,
    options?: { includeDeleted?: boolean },
  ): Promise<OfflineRecord | null> {
    return withDb(async (db) => {
      const result = await db.query(
        `SELECT * FROM ${table} WHERE id = ? LIMIT 1`,
        [id],
      );
      const row = (result.values?.[0] ?? null) as Record<string, unknown> | null;

      if (!row) {
        return null;
      }

      if (!options?.includeDeleted && row.deleted_at) {
        return null;
      }

      return rowToRecord(table, row);
    });
  }

  async setSyncStatus(
    table: OfflineEntityTable,
    id: string,
    status: SyncStatus,
  ): Promise<void> {
    await withDb(async (db) => {
      await db.run(`UPDATE ${table} SET sync_status = ?, updated_at = ? WHERE id = ?`, [
        status,
        nowIso(),
        id,
      ]);
    });
  }

  async enqueueSyncOperation(input: {
    table_name: OfflineEntityTable;
    record_id: string;
    operation: 'insert' | 'update' | 'delete';
    payload: Record<string, unknown>;
  }): Promise<string> {
    const queueId = generateFieldId();
    const createdAt = nowIso();

    await withDb(async (db) => {
      await db.run(
        `INSERT INTO sync_queue (id, table_name, record_id, operation, payload, created_at, attempts, last_error)
         VALUES (?, ?, ?, ?, ?, ?, 0, NULL)`,
        [
          queueId,
          input.table_name,
          input.record_id,
          input.operation,
          JSON.stringify(input.payload),
          createdAt,
        ],
      );
    });

    return queueId;
  }

  async listPendingQueue(limit = 50): Promise<
    Array<{
      id: string;
      table_name: string;
      record_id: string;
      operation: string;
      payload: Record<string, unknown>;
      created_at: string;
      attempts: number;
      last_error: string | null;
    }>
  > {
    return withDb(async (db) => {
      const result = await db.query(
        `SELECT * FROM sync_queue
         WHERE attempts < 3
         ORDER BY created_at ASC
         LIMIT ?`,
        [limit],
      );

      return ((result.values ?? []) as Record<string, unknown>[]).map((row) => ({
        id: String(row.id),
        table_name: String(row.table_name),
        record_id: String(row.record_id),
        operation: String(row.operation),
        payload: parseJson<Record<string, unknown>>(String(row.payload), {}),
        created_at: String(row.created_at),
        attempts: Number(row.attempts ?? 0),
        last_error: row.last_error ? String(row.last_error) : null,
      }));
    });
  }

  async countPendingQueue(): Promise<number> {
    return withDb(async (db) => {
      const result = await db.query(
        `SELECT COUNT(*) AS total FROM sync_queue WHERE attempts < 3`,
      );
      const row = result.values?.[0] as { total?: number } | undefined;

      return Number(row?.total ?? 0);
    });
  }

  async markQueueAttempt(queueId: string, error: string | null): Promise<void> {
    await withDb(async (db) => {
      await db.run(
        `UPDATE sync_queue
         SET attempts = attempts + 1, last_error = ?
         WHERE id = ?`,
        [error, queueId],
      );
    });
  }

  async removeQueueItem(queueId: string): Promise<void> {
    await withDb(async (db) => {
      await db.run(`DELETE FROM sync_queue WHERE id = ?`, [queueId]);
    });
  }

  async getMeta(key: string): Promise<string | null> {
    return withDb(async (db) => {
      const result = await db.query(`SELECT value FROM sync_meta WHERE key = ? LIMIT 1`, [
        key,
      ]);
      const row = result.values?.[0] as { value?: string } | undefined;

      return row?.value ?? null;
    });
  }

  async setMeta(key: string, value: string): Promise<void> {
    await withDb(async (db) => {
      await db.run(
        `INSERT INTO sync_meta (key, value) VALUES (?, ?)
         ON CONFLICT(key) DO UPDATE SET value = excluded.value`,
        [key, value],
      );
    });
  }

  async runQuery(sql: string, values: SqlValue[] = []): Promise<void> {
    await withDb(async (db) => {
      await db.run(sql, values);
    });
  }

  async queryRows<T extends Record<string, unknown>>(
    sql: string,
    values: SqlValue[] = [],
  ): Promise<T[]> {
    return withDb(async (db) => {
      const result = await db.query(sql, values);

      return (result.values ?? []) as T[];
    });
  }
}

export const databaseService = new DatabaseService();
